import React from "react";
import { LegalLayout, Section } from "../components/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      title="Terms & Conditions"
      subtitle="The rules of using PitchIQ. Plain English where possible. Please read before creating an account."
      lastUpdated="14 February 2026"
    >
      <Section id="acceptance" title="1. Acceptance of Terms">
        <p>
          By creating an account, accessing or using <strong>PitchIQ</strong> (the &quot;Service&quot;,
          operated from the United Kingdom and available at <a href="https://pitchiq.win" className="text-primary-yellow underline">pitchiq.win</a>),
          you agree to be bound by these Terms &amp; Conditions and our{" "}
          <a href="/privacy" className="text-primary-yellow underline">Privacy Policy</a>.
          If you do not agree, please do not use the Service.
        </p>
      </Section>

      <Section id="eligibility" title="2. Eligibility">
        <p>You must be at least <strong>18 years old</strong> to use PitchIQ. By creating an account you confirm:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>You are 18 or older.</li>
          <li>The information you provide is accurate and belongs to you.</li>
          <li>You are not creating multiple accounts to gain unfair advantage on leaderboards or competitions.</li>
          <li>You are not in a jurisdiction where the Service is prohibited.</li>
        </ul>
      </Section>

      <Section id="not-gambling" title="3. Not a Gambling Service">
        <p>
          PitchIQ is a <strong>free-to-play prediction skill game</strong>. We do not accept stakes,
          take bets, or pay out monetary prizes for predictions. The Premier season pass
          (£9.99) unlocks additional non-cash features (private leagues, full season fixture view,
          detailed stats) and is not a wager.
        </p>
        <p>
          <strong>Season 2026/27 has no cash or physical prizes.</strong> The season champion receives
          recognition only — their name etched onto the Premier Predictors Cup in our Hall of Champions,
          a digital winner&apos;s certificate, and bragging rights. Leaderboard positions, badges, and
          recognition are awarded based on prediction accuracy and are not exchangeable for cash.
        </p>
        <p>
          Future seasons may introduce prizes. If so, full prize terms will be published in advance and
          this section updated accordingly.
        </p>
      </Section>

      <Section id="account" title="4. Your Account">
        <p>
          You are responsible for keeping your password confidential and for all activity that occurs
          under your account. Notify us immediately at <a href="mailto:info@pitchiq.win" className="text-primary-yellow underline">info@pitchiq.win</a>{" "}
          if you suspect unauthorised access.
        </p>
        <p>
          We may suspend or terminate accounts that breach these Terms, exploit bugs, automate predictions,
          attempt to manipulate leaderboards, or harass other users — without refund for any active subscription.
        </p>
      </Section>

      <Section id="premier" title="5. Premier Subscription (£9.99)">
        <p>
          The Premier tier is a <strong>one-time payment of £9.99</strong> granting 365 days of
          enhanced access (private leagues, full season fixtures, stats dashboard, Premier badge).
          Payments are processed by Stripe. We never see or store your card details.
        </p>
        <p>
          Subscription does not auto-renew. After 365 days you revert to the free tier unless you choose
          to purchase again. See our <a href="/refund" className="text-primary-yellow underline">Refund Policy</a> for refund eligibility.
        </p>
      </Section>

      <Section id="conduct" title="6. Acceptable Use">
        <p>You agree not to:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Use bots, scrapers, or automated tools to submit predictions.</li>
          <li>Reverse-engineer, decompile, or attempt to extract source code from the Service.</li>
          <li>Upload or post content that is unlawful, abusive, discriminatory, or infringes third-party rights.</li>
          <li>Attempt to disrupt the Service, including via denial-of-service attacks or excessive API usage.</li>
          <li>Impersonate another person or create accounts using false identities.</li>
        </ul>
      </Section>

      <Section id="content" title="7. User Content">
        <p>
          Predictions, league names, usernames, and any other content you submit remain yours, but you grant
          PitchIQ a worldwide, royalty-free licence to display them within the Service (e.g. on leaderboards
          and within private leagues).
        </p>
      </Section>

      <Section id="third-party-data" title="8. Third-Party Data">
        <p>
          Fixture data is provided by <strong>API-Sports</strong>. Match results may occasionally be delayed
          or incorrect; we cannot guarantee real-time accuracy. Disputed scores will be corrected
          retrospectively where possible, and affected points recalculated automatically.
        </p>
        <p>
          PitchIQ is not affiliated with, endorsed by, or sponsored by The Premier League, any club, or
          API-Sports. All team names and badges remain the property of their respective owners.
        </p>
      </Section>

      <Section id="liability" title="9. Limitation of Liability">
        <p>
          To the maximum extent permitted by UK law, PitchIQ&apos;s total aggregate liability to you for any
          claim arising out of the Service is limited to the amount you have paid us in the 12 months
          preceding the claim (capped at £9.99 for free-tier users).
        </p>
        <p>
          We are not liable for indirect, incidental, or consequential damages, lost profits, data loss,
          or service interruption.
        </p>
      </Section>

      <Section id="termination" title="10. Termination">
        <p>
          You may delete your account at any time by contacting <a href="mailto:info@pitchiq.win" className="text-primary-yellow underline">info@pitchiq.win</a>.
          We may suspend or terminate accounts for breach of these Terms. On termination, your predictions
          may be retained anonymously for statistical purposes; personal data will be removed per our Privacy Policy.
        </p>
      </Section>

      <Section id="changes" title="11. Changes to These Terms">
        <p>
          We may update these Terms occasionally to reflect new features or legal requirements. Material
          changes will be announced via email at least 14 days before they take effect. Continued use of
          the Service after the effective date constitutes acceptance.
        </p>
      </Section>

      <Section id="scoring" title="12. Predictions & Scoring">
        <p>
          Points are awarded per prediction: exact score = 10 points plus goals-scored bonuses;
          correct outcome (win/draw) = 5 points; correct home or away goal tally = 1 point per correct tally.
        </p>
        <p>
          <strong>If you don&apos;t submit a prediction before kickoff, your pick defaults to 0-0.</strong>
        </p>
      </Section>

      <Section id="law" title="13. Governing Law">
        <p>
          These Terms are governed by the laws of <strong>England and Wales</strong>. Any disputes will be
          subject to the exclusive jurisdiction of the courts of England and Wales.
        </p>
      </Section>

      <Section id="contact" title="14. Contact">
        <p>
          Questions or complaints? Email <a href="mailto:info@pitchiq.win" className="text-primary-yellow underline">info@pitchiq.win</a>.
          We aim to respond within 5 working days.
        </p>
      </Section>
    </LegalLayout>
  );
}
